import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import apiKey from "../../../config/apiKey";
import i18n from "../../../i18n";
import en from "../../../i18n/locales/en.json";
import ar from "../../../i18n/locales/ar.json";

function flatten(obj, prefix = "") {
  let out = {};
  for (const k in obj) {
    const v = obj[k];
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flatten(v, key));
    } else {
      out[key] = v;
    }
  }
  return out;
}

const DEFAULT_EN = flatten(en);
const DEFAULT_AR = flatten(ar);
const ALL_KEYS = Object.keys(DEFAULT_EN).sort();
const PAGE_SIZE = 50;

function setAtPath(obj, dotPath, value) {
  const parts = dotPath.split(".");
  let node = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    node = node[parts[i]] = node[parts[i]] || {};
  }
  node[parts[parts.length - 1]] = value;
}

export default function Translations() {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));
  const headers = { Authorization: `Bearer ${admin?.token}` };

  const [overrides, setOverrides] = useState({}); // key -> { en, ar }
  const [drafts, setDrafts] = useState({}); // key -> { en, ar } (unsaved edits)
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyModified, setOnlyModified] = useState(false);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${apiKey}/api/edolutions/admin/translations`, { headers });
        const map = {};
        (res.data?.data || []).forEach((row) => {
          map[row.key] = { en: row.en, ar: row.ar };
        });
        setOverrides(map);
      } catch (err) {
        console.error("Failed to load translation overrides:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  const filteredKeys = useMemo(() => {
    let keys = ALL_KEYS;
    if (onlyModified) {
      keys = keys.filter((k) => overrides[k]);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      keys = keys.filter((k) => {
        const effEn = (overrides[k]?.en ?? DEFAULT_EN[k] ?? "").toString().toLowerCase();
        const effAr = (overrides[k]?.ar ?? DEFAULT_AR[k] ?? "").toString().toLowerCase();
        return k.toLowerCase().includes(q) || effEn.includes(q) || effAr.includes(q);
      });
    }
    return keys;
  }, [search, onlyModified, overrides]);

  const totalPages = Math.max(1, Math.ceil(filteredKeys.length / PAGE_SIZE));
  const pageKeys = filteredKeys.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getDraft = (key) => {
    if (drafts[key]) return drafts[key];
    const ov = overrides[key];
    return { en: ov?.en ?? DEFAULT_EN[key] ?? "", ar: ov?.ar ?? DEFAULT_AR[key] ?? "" };
  };

  const onEdit = (key, field, value) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...getDraft(key), [field]: value } }));
  };

  const applyLiveBundle = (key, enVal, arVal) => {
    const enPatch = {};
    const arPatch = {};
    setAtPath(enPatch, key, enVal);
    setAtPath(arPatch, key, arVal);
    i18n.addResourceBundle("en", "translation", enPatch, true, true);
    i18n.addResourceBundle("ar", "translation", arPatch, true, true);
    i18n.emit("languageChanged", i18n.language);
  };

  const save = async (key) => {
    const draft = getDraft(key);
    setSavingKey(key);
    try {
      await axios.put(`${apiKey}/api/edolutions/admin/translations`, { key, en: draft.en, ar: draft.ar }, { headers });
      setOverrides((prev) => ({ ...prev, [key]: { en: draft.en, ar: draft.ar } }));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      applyLiveBundle(key, draft.en, draft.ar);
      setToast(t("adminDash.translations.saved"));
    } catch (err) {
      console.error("Failed to save translation:", err);
      setToast(t("adminDash.translations.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const revert = async (key) => {
    setSavingKey(key);
    try {
      await axios.delete(`${apiKey}/api/edolutions/admin/translations/${encodeURIComponent(key)}`, { headers });
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      applyLiveBundle(key, DEFAULT_EN[key] ?? "", DEFAULT_AR[key] ?? "");
      setToast(t("adminDash.translations.reverted"));
    } catch (err) {
      console.error("Failed to revert translation:", err);
      setToast(t("adminDash.translations.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const modifiedCount = Object.keys(overrides).length;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-[#2A3A54]">{t("adminDash.translations.title")}</h1>
        <p className="text-sm text-black/60 mt-1">{t("adminDash.translations.subtitle", { count: ALL_KEYS.length, modified: modifiedCount })}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder={t("adminDash.translations.searchPlaceholder")}
          className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#EE5F2B]/40"
        />
        <label className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl border border-black/10 bg-white/70 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyModified}
            onChange={(e) => { setOnlyModified(e.target.checked); setPage(1); }}
          />
          {t("adminDash.translations.onlyModified")}
        </label>
      </div>

      {loading ? (
        <div className="text-center py-16 text-black/50">{t("adminDash.translations.loading")}</div>
      ) : filteredKeys.length === 0 ? (
        <div className="text-center py-16 text-black/50">{t("adminDash.translations.noResults")}</div>
      ) : (
        <>
          <div className="rounded-2xl border border-black/10 overflow-hidden bg-white">
            <div className="grid grid-cols-[minmax(180px,1fr)_1.4fr_1.4fr_auto] gap-3 px-4 py-2.5 bg-[#F7F7F5] text-xs font-semibold text-black/50 uppercase tracking-wide">
              <div>{t("adminDash.translations.colKey")}</div>
              <div>{t("adminDash.translations.colEnglish")}</div>
              <div>{t("adminDash.translations.colArabic")}</div>
              <div className="text-right">{t("adminDash.translations.colActions")}</div>
            </div>
            {pageKeys.map((key) => {
              const draft = getDraft(key);
              const isModified = !!overrides[key];
              const isDirty = !!drafts[key];
              const isSaving = savingKey === key;
              return (
                <div key={key} className="grid grid-cols-[minmax(180px,1fr)_1.4fr_1.4fr_auto] gap-3 px-4 py-2.5 border-t border-black/5 items-start">
                  <div className="text-xs font-mono text-black/60 break-all pt-2 flex items-start gap-1.5">
                    {isModified && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#EE5F2B] shrink-0" title={t("adminDash.translations.modifiedBadge")} />}
                    <span>{key}</span>
                  </div>
                  <textarea
                    value={draft.en}
                    onChange={(e) => onEdit(key, "en", e.target.value)}
                    rows={1}
                    className="w-full rounded-lg border border-black/10 px-2.5 py-1.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#EE5F2B]/30"
                  />
                  <textarea
                    dir="rtl"
                    value={draft.ar}
                    onChange={(e) => onEdit(key, "ar", e.target.value)}
                    rows={1}
                    className="w-full rounded-lg border border-black/10 px-2.5 py-1.5 text-sm resize-y text-right focus:outline-none focus:ring-2 focus:ring-[#EE5F2B]/30"
                  />
                  <div className="flex flex-col gap-1.5 items-end pt-1">
                    <button
                      disabled={!isDirty || isSaving}
                      onClick={() => save(key)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#EE5F2B] text-white disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSaving ? t("adminDash.translations.saving") : t("adminDash.translations.save")}
                    </button>
                    {isModified && (
                      <button
                        disabled={isSaving}
                        onClick={() => revert(key)}
                        className="text-xs text-black/50 hover:text-black/80 underline whitespace-nowrap"
                      >
                        {t("adminDash.translations.revert")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-black/60">
            <span>{t("adminDash.translations.pageInfo", { page, totalPages, total: filteredKeys.length })}</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-black/10 disabled:opacity-30"
              >
                {t("adminDash.translations.prev")}
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-lg border border-black/10 disabled:opacity-30"
              >
                {t("adminDash.translations.next")}
              </button>
            </div>
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-[#2A3A54] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
